import { useState } from 'react';
import { enviar } from '../api/cliente';
import {
  Aviso,
  Boton,
  Cajon,
  Cargando,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Vacio,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { fechaYHora } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import type { EvolucionVista } from '../api/tipos';

/**
 * Evoluciones en formato SOAP. Una evolución firmada no se edita: se enmienda, y el
 * original queda visible. La interfaz lo dice con todas las letras para que nadie espere
 * otra cosa.
 */
export function EvolucionesDelPaciente({ pacienteId }: { pacienteId: string }) {
  const { puede } = useSesion();
  const [firmando, setFirmando] = useState(false);
  const { datos, cargando, error, recargar } = useDatos<EvolucionVista[]>(
    `/api/v1/pacientes/${pacienteId}/evoluciones`,
  );

  if (cargando) return <Cargando que="las evoluciones" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  const dudosas = (datos ?? []).filter((e) => !e.integridadVerificada);

  return (
    <>
      {dudosas.length > 0 && (
        <div className="mb-4">
          <Aviso tono="alerta">
            {dudosas.length === 1
              ? 'Una evolución no verifica su firma de integridad.'
              : `${dudosas.length} evoluciones no verifican su firma de integridad.`}{' '}
            Avisá a la administración: el contenido no coincide con el hash que se firmó.
          </Aviso>
        </div>
      )}

      <Panel
        titulo="Evoluciones clínicas"
        descripcion="Formato SOAP. Cada una queda firmada y con su hash de integridad."
        accion={
          puede('historia_clinica.escribir') && (
            <Boton variante="primario" onClick={() => setFirmando(true)}>
              Nueva evolución
            </Boton>
          )
        }
      >
        {(!datos || datos.length === 0) && (
          <Vacio
            titulo="Sin evoluciones"
            detalle="La primera se firma al cerrar la sesión clínica del paciente."
            accion={
              puede('historia_clinica.escribir') && (
                <Boton variante="primario" onClick={() => setFirmando(true)}>
                  Nueva evolución
                </Boton>
              )
            }
          />
        )}

        {datos && datos.length > 0 && (
          <ol className="space-y-4">
            {datos.map((evolucion) => (
              <li key={evolucion.id} className="border-l-2 border-papel-300 pl-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-tinta-900">
                    {fechaYHora(evolucion.firmadaEn)}
                  </p>
                  <div className="flex items-center gap-2">
                    {evolucion.enmiendas > 0 && (
                      <Insignia tono="aviso">
                        {evolucion.enmiendas} {evolucion.enmiendas === 1 ? 'enmienda' : 'enmiendas'}
                      </Insignia>
                    )}
                    <Insignia tono={evolucion.integridadVerificada ? 'exito' : 'alerta'}>
                      {evolucion.integridadVerificada ? 'Íntegra' : 'No verifica'}
                    </Insignia>
                  </div>
                </div>

                <dl className="mt-2 space-y-1.5">
                  <Renglon rotulo="S" texto={evolucion.subjetivo} />
                  <Renglon rotulo="O" texto={evolucion.objetivo} />
                  <Renglon rotulo="A" texto={evolucion.analisis} />
                  <Renglon rotulo="P" texto={evolucion.plan} />
                </dl>

                {evolucion.piezasReferidas.length > 0 && (
                  <p className="mt-2 flex flex-wrap gap-1">
                    {evolucion.piezasReferidas.map((pieza) => (
                      <Insignia key={pieza} tono="marca">
                        {pieza}
                      </Insignia>
                    ))}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </Panel>

      <FirmarEvolucion
        abierto={firmando}
        pacienteId={pacienteId}
        alCerrar={() => setFirmando(false)}
        alFirmar={() => {
          setFirmando(false);
          recargar();
        }}
      />
    </>
  );
}

function Renglon({ rotulo, texto }: { rotulo: string; texto: string | null }) {
  if (!texto) {
    return null;
  }

  return (
    <div className="flex gap-2">
      <dt className="w-4 shrink-0 text-dato font-bold text-marca-600">{rotulo}</dt>
      <dd className="text-sm text-tinta-700">{texto}</dd>
    </div>
  );
}

function FirmarEvolucion({
  abierto,
  pacienteId,
  alCerrar,
  alFirmar,
}: {
  abierto: boolean;
  pacienteId: string;
  alCerrar: () => void;
  alFirmar: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [subjetivo, setSubjetivo] = useState('');
  const [objetivo, setObjetivo] = useState('');
  const [analisis, setAnalisis] = useState('');
  const [plan, setPlan] = useState('');
  const [piezas, setPiezas] = useState('');

  const firmar = async () => {
    const resultado = await correr(() =>
      enviar<{ id: string }>(`/api/v1/pacientes/${pacienteId}/evoluciones`, {
        pacienteId,
        // Sin sesión clínica abierta, la evolución se ata a una sesión nueva del servidor.
        sesionClinicaId: crypto.randomUUID().replace(/-/g, '').slice(0, 26).toUpperCase(),
        subjetivo: subjetivo.trim() || null,
        objetivo: objetivo.trim() || null,
        analisis: analisis.trim() || null,
        plan: plan.trim() || null,
        piezasReferidas: piezas
          .split(/[\s,]+/)
          .map((p) => p.trim())
          .filter(Boolean),
      }),
    );

    if (resultado) {
      setSubjetivo('');
      setObjetivo('');
      setAnalisis('');
      setPlan('');
      setPiezas('');
      alFirmar();
    }
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Nueva evolución"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void firmar()}>
            Firmar evolución
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="rounded-lg bg-aviso-50 px-3 py-2 text-dato text-aviso-700">
          Una vez firmada no se edita. Si hay que corregirla, se enmienda y el original queda
          visible.
        </p>

        <Area
          etiqueta="S — lo que refiere el paciente"
          valor={subjetivo}
          alCambiar={setSubjetivo}
          ejemplo="Dolor al frío en el sector superior derecho desde hace una semana."
        />
        <Area
          etiqueta="O — lo que observa el profesional"
          valor={objetivo}
          alCambiar={setObjetivo}
          ejemplo="Caries oclusal en 16, test de vitalidad positivo."
        />
        <Area etiqueta="A — diagnóstico" valor={analisis} alCambiar={setAnalisis} />
        <Area etiqueta="P — plan" valor={plan} alCambiar={setPlan} />

        <label className="block space-y-1">
          <span className="etiqueta-campo">Piezas referidas</span>
          <input
            type="text"
            value={piezas}
            onChange={(e) => setPiezas(e.target.value)}
            placeholder="16 26 36"
            className="campo"
          />
          <span className="block text-dato text-tinta-400">Notación FDI, separadas por espacio.</span>
        </label>

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function Area({
  etiqueta,
  valor,
  alCambiar,
  ejemplo,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (valor: string) => void;
  ejemplo?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="etiqueta-campo">{etiqueta}</span>
      <textarea
        rows={2}
        value={valor}
        placeholder={ejemplo}
        onChange={(e) => alCambiar(e.target.value)}
        className="campo"
      />
    </label>
  );
}
